/**
 * The Transaction Centre, decided: the registration fees a gallery's sales
 * have raised, and three counts pointing at the screens that own the rest.
 *
 * Pure. Fees, placements and settlements go in; the fee rows in their three
 * states, the counts and the fees-due line come out. The copy is
 * `transactions.constants.ts`; the three reads and the two-press paid
 * recording are `useTransactionsController.ts`. Awaiting-buyer sales and due
 * settlements are counted here and listed on Sold Works and Settlements,
 * never re-listed — two copies of a row with a control on one of them is how
 * a director acts on the wrong copy.
 *
 * Fees format in en-US to read the same as the Sell wizard's preview of the
 * same tariff, and a paid row says "recorded as paid" because nothing
 * observed a payment — a director said one was made.
 */

import type {
  BillingContactResponse,
  OrgMember,
  PlacedWork,
  RegistrationFee,
  Settlement,
} from "@ondo/shared";
import { formatMoney, settlementGroup, settlementState } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { BILLING_CONTACT_COPY, TRANSACTIONS_COPY } from "./transactions.constants.ts";
import { formatConsignedDate } from "./gallery.model.ts";
import { titleStateOf } from "./soldWorks.model.ts";

/** One fee, with every label already decided. */
export interface FeeRow {
  feeId: string;
  workId: string;
  title: string;
  /** 'John Currin · OND-K7PQ-3M2X'. */
  identity: string;
  /** 'Premium · covenant attached' or 'Base'. */
  tierLabel: string;
  /** '$350'. */
  amount: string;
  /** 'sold 14 Mar 2026'. */
  sale: string;
  state: "due" | "paid" | "void";
  stateLabel: string;
  /** 'Recorded as paid 20 Mar 2026 by Eleanor Ashgrove · Invoice 4471', or "". */
  note: string;
  href: string;
}

export type TransactionsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | {
      kind: "ready";
      /** The three counts across the top. Each links to the screen that owns it. */
      tiles: { awaitingBuyer: number; feesDue: number; settlementsDue: number };
      /** '$500 in fees due'. "" when nothing is. */
      feesDueSummary: string;
      due: FeeRow[];
      paid: FeeRow[];
      void: FeeRow[];
      empty: string | null;
    };

/**
 * Everything the screen renders, decided here.
 *
 * THREE SOURCES, ONE SCREEN. The Transaction Centre was described, in the
 * copy that stood in for it, as "the union of three things a sale produces —
 * a transfer waiting on its buyer, a registration fee due, and a settlement
 * outstanding". Two of those have screens of their own already (Sold Works
 * and Settlements), so here they are COUNTS that link out, and the fees —
 * which have no other screen — are the list. Rejected: re-listing the
 * awaiting-buyer sales and due settlements in full. Two copies of a row with
 * a control on one of them is how a director acts on the copy without the
 * control and concludes it is broken.
 */
export function toTransactionsView(input: {
  fees: RegistrationFee[] | null;
  placements: PlacedWork[] | null;
  settlements: Settlement[] | null;
  error: string | null;
  workBase: string;
}): TransactionsView {
  const { fees, placements, settlements, error, workBase } = input;

  if (error) return { kind: "error", title: TRANSACTIONS_COPY.errorTitle, message: error };
  // The fees are the subject; the other two are counts. The screen is readable
  // the moment the fees land, and a count can arrive a beat later.
  if (!fees) return { kind: "loading", message: TRANSACTIONS_COPY.loading };

  const rows = fees.map((fee) => toFeeRow(fee, workBase));
  const due = rows.filter((row) => row.state === "due");

  const feesDueMinor = fees
    .filter((fee) => fee.paidAt === null && fee.voidedAt === null)
    .reduce((sum, fee) => sum + fee.amountMinor, 0);
  const currency = fees[0]?.currency ?? "USD";

  return {
    kind: "ready",
    tiles: {
      awaitingBuyer: (placements ?? []).filter((p) => titleStateOf(p) === "awaiting").length,
      feesDue: due.length,
      settlementsDue: (settlements ?? []).filter(
        (s) => settlementGroup(settlementState(s)) === "due",
      ).length,
    },
    feesDueSummary:
      feesDueMinor > 0
        ? TRANSACTIONS_COPY.feesDueSummary(feeAmount(feesDueMinor, currency))
        : "",
    due,
    paid: rows.filter((row) => row.state === "paid"),
    void: rows.filter((row) => row.state === "void"),
    empty: rows.length === 0 ? TRANSACTIONS_COPY.noFees : null,
  };
}

/**
 * '$350' — en-US, to match the Sell wizard's preview of the same tariff.
 *
 * The settlements screens format in en-GB and would print 'US$350' here,
 * which is not wrong, but a director who saw '$350' on the fee step and
 * 'US$350' on the ledger would reasonably wonder whether they were the same
 * fee. They are, so they read the same.
 */
function feeAmount(minor: number, currency: string): string {
  return formatMoney({ minor, currency }, "en-US");
}

function toFeeRow(fee: RegistrationFee, workBase: string): FeeRow {
  const state = fee.voidedAt !== null ? "void" : fee.paidAt !== null ? "paid" : "due";

  return {
    feeId: fee.feeId,
    workId: fee.workId,
    title: fee.title,
    identity: [fee.attributedName, fee.ondoCode].filter(Boolean).join(" · "),
    tierLabel:
      fee.tier === "premium" ? TRANSACTIONS_COPY.tier.premium : TRANSACTIONS_COPY.tier.plain,
    amount: feeAmount(fee.amountMinor, fee.currency),
    sale: `${TRANSACTIONS_COPY.soldOn} ${formatConsignedDate(fee.soldAt)}`,
    state,
    stateLabel: TRANSACTIONS_COPY.state[state],
    note: describeNote(fee, state),
    href: `${workBase}/${fee.workId}`,
  };
}

/**
 * 'Recorded as paid 20 Mar 2026 by Eleanor Ashgrove · Invoice 4471'.
 *
 * RECORDED AS, and the words are the design. Nothing observed a payment;
 * a director said one was made. The name is who said it and the note is what
 * they said — the same disclosure `settle_resale()` makes on a settlement.
 */
export function describeNote(fee: RegistrationFee, state: FeeRow["state"]): string {
  if (state === "void") return TRANSACTIONS_COPY.voidNote;
  if (state !== "paid" || !fee.paidAt) return "";

  const by = fee.paidByName ? ` ${TRANSACTIONS_COPY.by} ${fee.paidByName}` : "";
  const note = fee.paidNote ? ` · ${fee.paidNote}` : "";

  return `${TRANSACTIONS_COPY.recordedPaid} ${formatConsignedDate(fee.paidAt)}${by}${note}`;
}

export function describeTransactionsError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 403) return TRANSACTIONS_COPY.errors.refused;
    if (cause.message) return cause.message;
  }

  return TRANSACTIONS_COPY.errorTitle;
}

/** One person the firm could route its fee notices to. */
export interface BillingCandidate {
  ondoId: string;
  /** 'Eleanor Ashgrove · Director' — the name, and the job title if there is one. */
  label: string;
}

export type BillingContactView =
  | { kind: "loading"; message: string }
  | { kind: "nobody"; message: string }
  | {
      kind: "ready";
      /** 'Eleanor Ashgrove · ONDO-1024'. */
      contact: string;
      contactOndoId: string;
      /** Whether the firm chose, or inherited the default. */
      standing: string;
      /** The firm's active seats, for the control. Empty when it cannot be read. */
      candidates: BillingCandidate[];
    };

/**
 * Who the firm's fee notices go to, decided.
 *
 * NOTHING HERE DERIVES THE DEFAULT. `ondo.billing_contact()` is the authority
 * for it and the API answers what it said; this only decides the sentence.
 * CLAUDE.md names the hazard in terms — "two copies of that rule drift" — and
 * the symptom would be this panel naming one person while the invoice went to
 * another.
 *
 * A NULL CONTACT IS A REAL STATE AND NOT AN ERROR. A firm with no active
 * administrator has nobody to invoice, which `20261005099400` decision 6 calls
 * an ordinary answer; the panel says so rather than showing a blank, and says
 * that the fees are still recorded, because that is the question somebody
 * reading it is actually asking.
 */
export function toBillingContactView(input: {
  contact: BillingContactResponse | null;
  members: OrgMember[] | null;
  error: string | null;
}): BillingContactView {
  if (input.error) return { kind: "nobody", message: input.error };
  if (!input.contact) return { kind: "loading", message: BILLING_CONTACT_COPY.loading };

  const { contactOndoId, contactName, chosen } = input.contact;
  if (!contactOndoId) return { kind: "nobody", message: BILLING_CONTACT_COPY.nobody };

  return {
    kind: "ready",
    contact: [contactName, contactOndoId].filter(Boolean).join(" · "),
    contactOndoId,
    standing: chosen ? BILLING_CONTACT_COPY.isChosen : BILLING_CONTACT_COPY.isDefault,
    candidates: (input.members ?? []).map((member) => ({
      ondoId: member.ondoId,
      label: [member.displayName, member.title].filter(Boolean).join(" · "),
    })),
  };
}
